import {
  ApiDashboardElement,
  Collection,
} from '@highimpact-frontend/api-types';
import { useDashboardHost } from '../../../../context/DashboardHostContext';
import { applyInitiatorSeeding } from '../../../../forms/initiatorCatalogSeeding';
import { rebuildInitiatorElementForType } from '../../../../forms/initiatorElementBuilders';
import { PlotFormProps } from '../../../../forms/InitiWidgetFormHandler';
import { useInitiatorCatalogBundle } from '../../../../forms/useInitiatorCatalogBundle';
import { getElementType } from '../../../../state/dashboard-element-operations';
import { widgetCreationInfo } from '../../useWidgetCreationInfo';
import {
  BuiltinWidgetGroupId,
  getBuiltinGroupConfig,
} from '../../widgetGroups';
import UnhandledForm from './UnHandledForm';
import WidgetTypeInitiatorForm from './WidgetTypeInitiatorForm';

interface BuiltinGroupInitiatorFormProps extends PlotFormProps {
  groupId: BuiltinWidgetGroupId;
  widgets: Record<string, widgetCreationInfo>;
}

function BuiltinGroupInitiatorForm({
  groupId,
  widgets,
  start_time,
  end_time,
  project_id,
  site_id,
  ...formProps
}: BuiltinGroupInitiatorFormProps) {
  const host = useDashboardHost();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const groupConfig = getBuiltinGroupConfig(groupId)!;
  const { bundle } = useInitiatorCatalogBundle({ project_id, site_id });

  const preserveUnconfiguredForType = (typeId: string) =>
    !['text', 'image', 'imagegallery'].includes(typeId);

  const applyTypeChange = (
    typeId: string,
    config: ApiDashboardElement
  ): ApiDashboardElement => {
    const elementType = typeId as Exclude<
      ApiDashboardElement['element_type'],
      'collection' | 'readonlytext'
    >;

    const rebuilt = rebuildInitiatorElementForType(
      elementType,
      config as Exclude<ApiDashboardElement, Collection>,
      {
        host,
        startDate: start_time,
        endDate: end_time,
        preserveUnconfigured: preserveUnconfiguredForType(typeId),
      }
    );

    return applyInitiatorSeeding(elementType, rebuilt, {
      bundle,
      host,
      startDate: start_time,
      endDate: end_time,
      plugins: [],
      preserveUnconfigured: preserveUnconfiguredForType(typeId),
    });
  };

  return (
    <WidgetTypeInitiatorForm
      {...formProps}
      project_id={project_id}
      site_id={site_id}
      start_time={start_time}
      end_time={end_time}
      typeSelectorLabel={groupConfig.typeSelectorLabel}
      options={groupConfig.members.map((member) => ({
        id: member,
        label: widgets[member]?.label ?? member,
      }))}
      getSelectedTypeId={(config) =>
        getElementType(config) ?? groupConfig.members[0]
      }
      onTypeChange={applyTypeChange}
    />
  );
}

interface BuiltinGroupInitiatorFormRouterProps extends PlotFormProps {
  groupId: string;
  widgets: Record<string, widgetCreationInfo>;
}

function BuiltinGroupInitiatorFormRouter({
  groupId,
  ...props
}: BuiltinGroupInitiatorFormRouterProps) {
  const groupConfig = getBuiltinGroupConfig(groupId);
  if (!groupConfig) {
    return <UnhandledForm />;
  }

  return (
    <BuiltinGroupInitiatorForm
      groupId={groupId as BuiltinWidgetGroupId}
      {...props}
    />
  );
}

export default BuiltinGroupInitiatorFormRouter;
